/**
 * Template editor page.
 *
 * Layout (all app-theme blue):
 *   1. Back-strip: navigates back to where the user came from.
 *   2. Metadata form: name (required + unique), modality (required),
 *      body part (optional). Values stay editable while the body is
 *      worked on and are the values the Save button uses.
 *   3. ReportEditor in template mode: no header, just the body.
 *
 * Back navigation is bulletproof via TWO mechanisms working together:
 *   - `location.state.from` tells us the origin URL.
 *   - `sessionStorage.reopenSettingsOn = 'templates'` (set by
 *      SettingsDialog before it navigates) tells WorklistPage to
 *      reopen the Settings modal on the Templates tab. WorklistPage
 *      clears the flag once it acts on it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiCall } from '../api/client';
import ReportEditor, { TemplateMeta } from '../components/ReportEditor';

const MODALITY_OPTIONS = ['CT', 'MR', 'CR', 'DX', 'US', 'MG', 'NM', 'PT', 'XA', 'RF', 'OT'];

interface TemplateRow {
  templateId: number;
  name: string;
  modality?: string;
  bodyPart?: string;
}

export default function TemplatePage() {
  const { templateId: templateIdParam } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = templateIdParam === 'new';
  const templateId = isNew ? 0 : Number(templateIdParam);

  const [meta, setMeta] = useState<TemplateMeta | null>(
    isNew ? { templateId: 0, name: '', modality: '', bodyPart: '' } : null
  );
  const [loading, setLoading] = useState(!isNew);
  const [otherNames, setOtherNames] = useState<string[]>([]);

  useEffect(() => {
    document.title = isNew ? 'New Template' : 'Edit Template';
    apiCall<TemplateRow>('templates.list', { modality: '' }).then((res) => {
      if (res.s !== 'ok') { setLoading(false); return; }
      const list = res.data || [];
      const others = list.filter((t) => t.templateId !== templateId).map((t) => t.name);
      setOtherNames(others);
      if (!isNew) {
        const t = list.find((x) => x.templateId === templateId);
        if (t) {
          setMeta({
            templateId: t.templateId,
            name: t.name,
            modality: t.modality || '',
            bodyPart: t.bodyPart || '',
          });
          document.title = `Template - ${t.name}`;
        }
      }
      setLoading(false);
    });
  }, [templateId, isNew]);

  const nameError = useMemo(() => {
    const n = (meta?.name || '').trim();
    if (!n) return 'Name is required.';
    if (otherNames.some((existing) => existing.toLowerCase() === n.toLowerCase())) {
      return 'A template with this name already exists.';
    }
    return '';
  }, [meta?.name, otherNames]);

  const modalityError = (meta?.modality || '').trim() === '' ? 'Modality is required.' : '';

  function goBack() {
    const guard = (window as any).__reportEditorCloseGuard;
    if (typeof guard === 'function' && !guard()) return;

    const from = (location.state as any)?.from;
    // The origin's "reopen" hint (set by SettingsDialog / ManageTemplatesDialog
    // before navigating here) tells the destination which modal to pop
    // back open on mount. Two known values:
    //   'templates' -> reopen Settings dialog on the Templates tab
    //   'templateManager' -> reopen ManageTemplatesDialog in the report editor
    const reopen = (location.state as any)?.reopen;
    if (reopen) {
      try { sessionStorage.setItem('reopenAfterTemplate', reopen); } catch { /* private mode */ }
    }
    if (typeof from === 'string' && from) navigate(from);
    else if (window.history.length > 1) navigate(-1);
    else navigate('/worklist');
  }

  if (loading || !meta) return <div style={{ padding: 20 }}>Loading template...</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f7fafc' }}>
      {/* Back strip -- app blue (matches ReportEditor header) */}
      <div style={{
        padding: '8px 14px',
        background: '#1a4971',
        color: 'white',
        fontSize: 12,
        display: 'flex',
        gap: 16,
        alignItems: 'center',
      }}>
        <button
          onClick={goBack}
          style={{
            background: 'rgba(255,255,255,0.15)', color: 'white',
            border: '1px solid rgba(255,255,255,0.3)', borderRadius: 3,
            padding: '4px 12px',
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          &lt; Back
        </button>
        <span style={{ fontWeight: 700 }}>{isNew ? 'New Template' : 'Editing Template'}</span>
        {meta.name && !isNew && <span style={{ opacity: 0.85 }}>&nbsp;-&nbsp;{meta.name}</span>}
      </div>

      {/* Meta form -- light background, app blue accents */}
      <div style={{
        padding: '12px 14px',
        background: '#edf2f7',
        borderBottom: '1px solid #cbd5e0',
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
        fontSize: 12,
        flexWrap: 'wrap',
      }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 240px', minWidth: 200 }}>
          <span style={{ fontWeight: 600, color: '#2d3748' }}>
            Template Name <span style={{ color: '#c53030' }}>*</span>
          </span>
          <input
            style={{
              padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: 3,
              fontSize: 12, background: 'white',
            }}
            value={meta.name}
            placeholder="e.g. CT Chest - Normal"
            onChange={(e) => setMeta({ ...meta, name: e.target.value })}
          />
          {nameError && <span style={{ color: '#c53030', fontSize: 11 }}>{nameError}</span>}
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 160 }}>
          <span style={{ fontWeight: 600, color: '#2d3748' }}>
            Modality <span style={{ color: '#c53030' }}>*</span>
          </span>
          <select
            style={{
              padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: 3,
              fontSize: 12, background: 'white',
            }}
            value={MODALITY_OPTIONS.includes(meta.modality || '') ? (meta.modality || '') : (meta.modality ? '__other__' : '')}
            onChange={(e) => {
              const v = e.target.value;
              setMeta({ ...meta, modality: v === '__other__' ? '' : v });
            }}
          >
            <option value="">Select...</option>
            {MODALITY_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            <option value="__other__">Other...</option>
          </select>
          {!MODALITY_OPTIONS.includes(meta.modality || '') && meta.modality !== undefined && (
            <input
              style={{
                padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: 3,
                fontSize: 12, background: 'white', marginTop: 2,
              }}
              value={meta.modality || ''}
              onChange={(e) => setMeta({ ...meta, modality: e.target.value.toUpperCase() })}
              placeholder="Custom code"
            />
          )}
          {modalityError && <span style={{ color: '#c53030', fontSize: 11 }}>{modalityError}</span>}
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 200px', minWidth: 180 }}>
          <span style={{ fontWeight: 600, color: '#2d3748' }}>Body Part (optional)</span>
          <input
            style={{
              padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: 3,
              fontSize: 12, background: 'white',
            }}
            value={meta.bodyPart || ''}
            placeholder="e.g. Chest, Abdomen"
            onChange={(e) => setMeta({ ...meta, bodyPart: e.target.value })}
          />
        </label>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <ReportEditor
          mode="template"
          templateId={templateId}
          templateMeta={meta}
          fullScreen
        />
      </div>
    </div>
  );
}
